import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveIcon from '@mui/icons-material/Remove';
import { Alert, Box, CircularProgress, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import * as React from 'react';

import { errorCode, OnboardingApiError } from './api';
import { errorWords, LANE_STATES } from './labels';
import { plainSummary } from './products';
import type { OnboardingAttention, OnboardingLane, OnboardingLaneState } from './types';

export const Section = ({ title, children, testId, action }: { title?: React.ReactNode; children: React.ReactNode; testId?: string; action?: React.ReactNode }) => (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }} data-testid={testId}>
        {(title || action) && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                {title && <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 500, flexGrow: 1 }}>{title}</Typography>}
                {action}
            </Stack>
        )}
        {children}
    </Paper>
);

/** A lane's state as an icon (colour only reinforces it; the words beside it say the state). */
export function LaneStateIcon({ state, size = 18 }: { state: OnboardingLaneState | string; size?: number }) {
    const sx = { fontSize: size, flex: 'none', display: 'block' } as const;
    switch (state) {
        case 'running':
            return <CircularProgress aria-hidden size={size - 4} thickness={5} sx={{ m: '2px', flex: 'none' }} />;
        case 'done':
            return <CheckCircleOutlineIcon aria-hidden sx={{ ...sx, color: 'success.main' }} />;
        case 'attention':
            return <PriorityHighIcon aria-hidden sx={{ ...sx, color: 'warning.dark' }} />;
        case 'error':
            return <ErrorOutlineIcon aria-hidden sx={{ ...sx, color: 'error.main' }} />;
        case 'skipped':
            return <RemoveIcon aria-hidden sx={{ ...sx, color: 'text.disabled' }} />;
        default:
            return <RadioButtonUncheckedIcon aria-hidden sx={{ ...sx, color: 'text.disabled' }} />;
    }
}

/** A lane's words: its summary (no trailing period), else the state's own word. */
export const laneWords = (lane: Pick<OnboardingLane, 'state' | 'summary'>) =>
    plainSummary(lane.summary) || LANE_STATES[lane.state] || String(lane.state);

export function LaneProgress({ lane }: { lane: OnboardingLane }) {
    if (lane.state !== 'running') {
        return null;
    }
    const { progress } = lane;
    if (progress && progress.total > 0) {
        const value = Math.min(100, Math.round((100 * progress.current) / progress.total));
        return <LinearProgress variant="determinate" value={value} aria-label={`${lane.label}: ${progress.current} of ${progress.total}`} sx={{ mt: 0.5 }} />;
    }
    return <LinearProgress aria-label={`${lane.label}: in progress`} sx={{ mt: 0.5 }} />;
}

/** One lane: icon, label, words and, while it runs, its progress. */
export function LaneRow({ lane }: { lane: OnboardingLane }) {
    return (
        <Box data-testid={`lane-${lane.key}`} sx={{ display: 'grid', gridTemplateColumns: '22px minmax(0,1fr)', columnGap: 1, alignItems: 'start', py: 0.75 }}>
            <Box sx={{ pt: '2px' }}><LaneStateIcon state={lane.state} /></Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{lane.label}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                    {laneWords(lane)}
                </Typography>
                <LaneProgress lane={lane} />
            </Box>
        </Box>
    );
}

/** The setup lanes, in the backend's order, with a count of the finished ones. */
export function ProgressPanel({ lanes }: { lanes: OnboardingLane[] }) {
    const finished = lanes.filter((l) => l.state === 'done' || l.state === 'skipped').length;
    return (
        <Section
            title="Setup progress"
            testId="progress-panel"
            action={<Typography variant="caption" color="text.secondary">{`${finished} of ${lanes.length} done`}</Typography>}
        >
            <Box>
                {lanes.map((lane) => <LaneRow key={lane.key} lane={lane} />)}
            </Box>
        </Section>
    );
}

/** What needs the client or our team, in the client's words, by lane. */
export function AttentionList({ attention, lanes }: { attention: OnboardingAttention[]; lanes: OnboardingLane[] }) {
    if (!attention.length) {
        return null;
    }
    const labelOf = (key: string) => lanes.find((l) => l.key === key)?.label || key;
    return (
        <Stack spacing={1} data-testid="attention-list">
            {attention.map((a, i) => (
                <Alert key={`${a.lane}-${i}`} severity="warning">
                    <strong>{labelOf(a.lane)}:</strong> {a.text}
                </Alert>
            ))}
        </Stack>
    );
}

/** The words for a failed action: the page's own for the contract's codes (`own` first, for codes
 *  worded by context), else the server's detail. */
export function actionErrorText(error: unknown, own: Record<string, string> = {}): string {
    const code = errorCode(error);
    if (code && own[code]) {
        return own[code];
    }
    if (error instanceof OnboardingApiError) {
        if (code === 'rate_limited' && error.retryAfter) {
            const hours = Math.ceil(error.retryAfter / 3600);
            return `You've checked products many times today. Try again in ${hours === 1 ? 'an hour' : `${hours} hours`}.`;
        }
        return errorWords(code, error.message);
    }
    return 'That did not go through. Try again.';
}

export const ErrorLine = ({ error, own, testId }: { error: unknown; own?: Record<string, string>; testId?: string }) => (
    error ? <Alert severity="error" data-testid={testId} sx={{ my: 1 }}>{actionErrorText(error, own)}</Alert> : null
);
